/**
 * Document Editor Page
 * Three-panel layout: Table of Contents | Read/Edit View | Info/Comments/History
 * - Read mode (default): continuous document with all sections
 * - Edit mode: Quill rich text editor with toolbar + inline comments
 * - Collapsible side panels with expand/collapse icons inside panels
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BaseLayout } from '@/layouts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  ArrowLeft,
  Save,
  Wand2,
  CheckCircle2,
  Circle,
  FileText,
  Send,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  History,
  Info,
  AlertCircle,
  Loader2,
  X,
  Reply,
  Check,
  Edit,
  Eye,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  List,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useDocuments } from '@/hooks/useDocuments';
import { ROUTES } from '@/constants/routes';
import {
  DOCUMENT_STATUS,
  SECTION_STATUS,
  MOCK_USERS,
  USER_ROLES,
  MOCK_INLINE_COMMENTS,
  formatDate,
  formatDateTime,
  getRelativeTime,
} from '@/constants/beStudyMockData';
import { DocumentStatusBadge, SectionStatusBadge } from '@/components/be-study/common/StatusBadge';
import { SimpleProgressBar } from '@/components/be-study/common/ProgressBar';
import { ConfidenceBadge } from '@/components/be-study/common/ConfidenceIndicator';
import { RichTextEditor } from '@/components/be-study/editor/RichTextEditor';
import { Doc003ContinuousView, Doc003DynamicToc, Doc003CommentSidebar } from '@/components/be-study/editor/Doc003ContinuousView';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

/* ── Left Panel: Table of Contents ── */
function TableOfContents({ sections, activeId, onSelect, progress, onCollapse }) {
  const statusIcon = (status) => {
    switch (status) {
      case SECTION_STATUS.COMPLETED:
        return <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />;
      case SECTION_STATUS.IN_PROGRESS:
        return <Loader2 className="h-3.5 w-3.5 text-blue-500 shrink-0 animate-spin" />;
      case SECTION_STATUS.NEEDS_REVISION:
        return <AlertCircle className="h-3.5 w-3.5 text-orange-500 shrink-0" />;
      default:
        return <Circle className="h-3.5 w-3.5 text-muted-foreground/40 shrink-0" />;
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="table-of-contents">
      {/* Header with collapse icon */}
      <div className="p-3 border-b flex items-center justify-between">
        <p className="text-xs font-semibold uppercase text-muted-foreground tracking-wide">
          Table of Contents
        </p>
        <button
          onClick={onCollapse}
          className="p-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          title="Collapse panel"
          data-testid="collapse-left-btn"
        >
          <List className="h-4 w-4" />
        </button>
      </div>
      <div className="px-3 pt-2 pb-1">
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
          <span>Progress</span>
        </div>
        <SimpleProgressBar value={progress} size="sm" />
      </div>
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-0.5">
          {sections.map((s, idx) => (
            <button
              key={s.id}
              onClick={() => onSelect(s.id)}
              className={cn(
                'w-full flex items-start gap-2 px-2.5 py-2.5 rounded-md text-left text-sm transition-colors',
                activeId === s.id
                  ? 'bg-primary/10 text-primary font-medium border-l-2 border-primary'
                  : 'hover:bg-muted text-foreground'
              )}
              data-testid={`toc-section-${s.id}`}
            >
              {statusIcon(s.status)}
              <div className="flex-1 min-w-0">
                <span className="truncate block text-xs">{idx + 1}. {s.title}</span>
                <div className="flex items-center gap-2 mt-1">
                  {s.confidenceScore > 0 && (
                    <ConfidenceBadge score={s.confidenceScore} size="sm" />
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}

/* ── Collapsed Left Panel Strip ── */
function CollapsedLeftStrip({ onExpand }) {
  return (
    <div className="flex flex-col items-center pt-3 w-10 border-r bg-card" data-testid="collapsed-left-strip">
      <button
        onClick={onExpand}
        className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
        title="Expand Table of Contents"
        data-testid="expand-left-btn"
      >
        <List className="h-4 w-4" />
      </button>
    </div>
  );
}

/* ── Collaborator Avatars ── */
const AVATAR_COLORS = ['bg-blue-600', 'bg-pink-500', 'bg-amber-500', 'bg-emerald-500', 'bg-violet-500'];

function CollaboratorAvatars({ doc }) {
  const collaborators = useMemo(() => {
    const people = [];
    if (doc.createdByName) {
      people.push({ name: doc.createdByName, id: doc.createdBy });
    }
    if (doc.assignedReviewerName) {
      people.push({ name: doc.assignedReviewerName, id: doc.assignedReviewer });
    }
    // Add admin as implicit collaborator
    const admin = MOCK_USERS.find(u => u.role === USER_ROLES.ADMIN);
    if (admin && admin.id !== doc.createdBy && admin.id !== doc.assignedReviewer) {
      people.push({ name: admin.name, id: admin.id });
    }
    return people;
  }, [doc]);

  const getInitials = (name) => {
    const parts = name.split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  const visible = collaborators.slice(0, 3);
  const extra = collaborators.length - 3;

  return (
    <div className="flex items-center -space-x-2 mr-2" data-testid="collaborator-avatars">
      {visible.map((person, idx) => (
        <div
          key={person.id}
          className={cn(
            'w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white ring-2 ring-background',
            AVATAR_COLORS[idx % AVATAR_COLORS.length]
          )}
          title={person.name}
          data-testid={`collaborator-avatar-${person.id}`}
        >
          {getInitials(person.name)}
        </div>
      ))}
      {extra > 0 && (
        <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-medium text-muted-foreground bg-muted ring-2 ring-background" data-testid="collaborator-extra">
          +{extra}
        </div>
      )}
    </div>
  );
}

/* ── Collapsed Right Panel Strip ── */
function CollapsedRightStrip({ onExpand }) {
  return (
    <div className="flex flex-col items-center pt-3 w-10 border-l bg-card" data-testid="collapsed-right-strip">
      <button
        onClick={onExpand}
        className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
        title="Expand panel"
        data-testid="expand-right-btn"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
    </div>
  );
}

/* ── Highlighted Content with Inline Comments ── */
function HighlightedContent({ content, inlineComments, sectionId }) {
  const [hoveredComment, setHoveredComment] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const sectionComments = useMemo(
    () => inlineComments.filter(c => c.sectionId === sectionId),
    [inlineComments, sectionId]
  );

  if (!sectionComments.length) {
    return (
      <div className="text-sm leading-relaxed text-foreground/90 whitespace-pre-wrap columns-1 md:columns-2 gap-8">
        {content}
      </div>
    );
  }

  // Build fragments: split content by commented text
  const buildFragments = () => {
    let remaining = content;
    const parts = [];
    // Sort comments by position in text
    const sorted = [...sectionComments].sort(
      (a, b) => remaining.indexOf(a.selectedText) - remaining.indexOf(b.selectedText)
    );
    for (const comment of sorted) {
      const idx = remaining.indexOf(comment.selectedText);
      if (idx === -1) continue;
      if (idx > 0) parts.push({ type: 'text', value: remaining.slice(0, idx) });
      parts.push({ type: 'comment', value: comment.selectedText, comment });
      remaining = remaining.slice(idx + comment.selectedText.length);
    }
    if (remaining) parts.push({ type: 'text', value: remaining });
    return parts;
  };

  const fragments = buildFragments();

  const handleMouseEnter = (e, comment) => {
    const rect = e.target.getBoundingClientRect();
    setTooltipPos({ x: rect.left, y: rect.bottom + 4 });
    setHoveredComment(comment);
  };

  return (
    <div className="text-sm leading-relaxed text-foreground/90 whitespace-pre-wrap relative">
      {fragments.map((f, i) =>
        f.type === 'text' ? (
          <span key={i}>{f.value}</span>
        ) : (
          <span
            key={i}
            className="bg-amber-100 border-b-2 border-amber-400 cursor-pointer rounded-sm px-px"
            onMouseEnter={(e) => handleMouseEnter(e, f.comment)}
            onMouseLeave={() => setHoveredComment(null)}
            data-testid={`inline-highlight-${f.comment.id}`}
          >
            {f.value}
          </span>
        )
      )}
      {hoveredComment && (
        <div
          className="fixed z-50 max-w-xs bg-popover border shadow-lg rounded-lg p-3 text-xs pointer-events-none"
          style={{ left: tooltipPos.x, top: tooltipPos.y }}
          data-testid="inline-comment-tooltip"
        >
          <div className="flex items-center gap-1.5 mb-1.5">
            <div className="w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center text-[9px] font-bold text-white shrink-0">
              {hoveredComment.authorName?.charAt(0)}
            </div>
            <span className="font-semibold text-foreground">{hoveredComment.authorName}</span>
            <span className="text-muted-foreground ml-auto">{hoveredComment.authorRole}</span>
          </div>
          <p className="text-foreground/80 leading-relaxed">{hoveredComment.text}</p>
        </div>
      )}
    </div>
  );
}

/* ── Read-Only Document View ── */
function ReadOnlyDocument({ sections, onScrollToSection, inlineComments }) {
  return (
    <ScrollArea className="h-full">
      <div className="p-8 max-w-4xl mx-auto" data-testid="readonly-document">
        <h1 className="text-2xl font-bold text-primary mb-6">Clinical Document</h1>
        {sections.map((section, idx) => (
          <div key={section.id} id={`section-${section.id}`} className="mb-8" data-testid={`readonly-section-${section.id}`}>
            <h2 className="text-xl font-semibold text-primary mb-3">{section.title}</h2>
            {section.content ? (
              <HighlightedContent
                content={section.content}
                inlineComments={inlineComments}
                sectionId={section.id}
              />
            ) : (
              <p className="text-sm text-muted-foreground italic">No content yet.</p>
            )}
            {section.images && section.images.length > 0 && (
              <div className="mt-4 space-y-4" data-testid={`section-images-${section.id}`}>
                {section.images.map((img, imgIdx) => (
                  <figure key={imgIdx} className="border rounded-lg overflow-hidden bg-white">
                    <img
                      src={img.src}
                      alt={img.caption}
                      className="w-full h-auto"
                      data-testid={`section-image-${section.id}-${imgIdx}`}
                    />
                    {img.caption && (
                      <figcaption className="text-xs text-muted-foreground text-center py-2 px-3 bg-muted/30 border-t">
                        {img.caption}
                      </figcaption>
                    )}
                  </figure>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </ScrollArea>
  );
}

/* ── Center Panel ── */
function CenterPanel({
  doc,
  sections,
  isEditMode,
  editorHtml,
  onEditorUpdate,
  onAddInlineComment,
  inlineComments,
  authorName,
  onSwitchToEdit,
  onSwitchToRead,
  onSave,
  onGenerate,
  isSaving,
  isGenerating,
  canEdit,
  onScrollToSection,
  activeSectionId,
  onDoc003TocReady,
  onDoc003CommentsChange,
  currentUser,
}) {
  const isDoc003 = doc?.id === 'DOC-003';

  return (
    <div className="flex flex-col h-full relative" data-testid="center-panel">
      {isEditMode ? (
        <>
          {/* Edit mode header */}
          <div className="flex items-center justify-between px-4 py-2 border-b bg-background shrink-0">
            {/* <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">Edit Mode</Badge>
              {isDoc003 && <Badge variant="secondary" className="text-xs">TipTap</Badge>}
            </div> */}
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={onSave} disabled={isSaving} data-testid="save-btn">
                {isSaving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                Save
              </Button>
              <Button variant="ghost" size="sm" onClick={onSwitchToRead} data-testid="exit-edit-btn">
                <Eye className="h-4 w-4 mr-1" /> Read Mode
              </Button>
            </div>
          </div>
          {/* Editor: Continuous TipTap for DOC-003, Quill for all others */}
          <div className="flex-1 overflow-hidden">
            {isDoc003 ? (
              <Doc003ContinuousView
                isEditMode={true}
                onUpdate={onEditorUpdate}
                onTocReady={onDoc003TocReady}
                onCommentsChange={onDoc003CommentsChange}
                currentUser={currentUser}
              />
            ) : (
              <RichTextEditor
                content={editorHtml}
                onUpdate={onEditorUpdate}
                onAddInlineComment={onAddInlineComment}
                inlineComments={inlineComments}
                authorName={authorName}
              />
            )}
          </div>
        </>
      ) : (
        <>
          {/* Read mode with Edit button */}
          {canEdit && (
            <div className="flex items-center justify-end px-4 py-2 border-b bg-background shrink-0">
              <Button size="sm" onClick={onSwitchToEdit} data-testid="enter-edit-btn">
                <Edit className="h-4 w-4 mr-1" /> Edit
              </Button>
            </div>
          )}
          {isDoc003 ? (
            <Doc003ContinuousView
              isEditMode={false}
              onTocReady={onDoc003TocReady}
              onCommentsChange={onDoc003CommentsChange}
              currentUser={currentUser}
            />
          ) : (
            <ReadOnlyDocument sections={sections} onScrollToSection={onScrollToSection} inlineComments={inlineComments} />
          )}
        </>
      )}
    </div>
  );
}

/* ── Collapsible Card Section ── */
function CollapsibleCard({ title, icon: Icon, badge, defaultOpen = true, children, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border rounded-lg bg-card overflow-hidden" data-testid={testId}>
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-muted/50 transition-colors"
        data-testid={`${testId}-toggle`}
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
          <span className="text-sm font-semibold">{title}</span>
          {badge}
        </div>
        <ChevronDown className={cn('h-4 w-4 text-muted-foreground transition-transform', open && 'rotate-180')} />
      </button>
      {open && <div className="border-t">{children}</div>}
    </div>
  );
}

/* ── Right Panel: Collapsible Cards ── */
function DetailPanel({
  onCollapse,
  document: doc,
  comments,
  versions,
  onAddComment,
  onReplyComment,
  onResolveComment,
  currentUser,
  activeSectionId,
  sections,
}) {
  const [newComment, setNewComment] = useState('');
  const [replyTargetId, setReplyTargetId] = useState(null);
  const [replyText, setReplyText] = useState('');

  const sectionComments = activeSectionId
    ? comments.filter(c => c.sectionId === activeSectionId)
    : comments;

  const handleAddComment = () => {
    if (!newComment.trim()) return;
    onAddComment({
      sectionId: activeSectionId || sections?.[0]?.id,
      author: currentUser.id,
      authorName: currentUser.name,
      authorRole: currentUser.role,
      text: newComment.trim(),
    });
    setNewComment('');
    toast.success('Comment added');
  };

  const handleReply = (commentId) => {
    if (!replyText.trim()) return;
    onReplyComment(commentId, {
      author: currentUser.id,
      authorName: currentUser.name,
      text: replyText.trim(),
    });
    setReplyTargetId(null);
    setReplyText('');
    toast.success('Reply added');
  };

  return (
    <div className="flex flex-col h-full" data-testid="detail-panel">
      {/* Collapse button */}
      <div className="flex items-center justify-end p-2 border-b shrink-0">
        <button
          onClick={onCollapse}
          className="p-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          title="Collapse panel"
          data-testid="collapse-right-btn"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-3 space-y-3">

          {/* Document Info Card */}
          <CollapsibleCard title="Document Info" icon={Info} defaultOpen={true} testId="card-info">
            <div className="p-3 text-sm">
              <div className="space-y-2">
                <div className="flex justify-between"><span className="text-muted-foreground">Template</span><span className="font-medium text-right max-w-[120px] truncate">{doc.templateName}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Author</span><span>{doc.createdByName}</span></div>
                {doc.assignedReviewerName && <div className="flex justify-between"><span className="text-muted-foreground">Reviewer</span><span>{doc.assignedReviewerName}</span></div>}
                {doc.dueDate && <div className="flex justify-between"><span className="text-muted-foreground">Due Date</span><span className="text-green-600 font-medium">{formatDate(doc.dueDate)}</span></div>}
                <div className="flex justify-between"><span className="text-muted-foreground">Created</span><span>{formatDate(doc.createdAt)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Updated</span><span>{formatDate(doc.updatedAt)}</span></div>
              </div>
            </div>
          </CollapsibleCard>

          {/* Document Version Card */}
          <CollapsibleCard
            title="Document Version"
            icon={History}
            defaultOpen={true}
            testId="card-version"
            badge={versions.length > 0 ? <Badge variant="secondary" className="h-4 px-1 text-[10px]">{versions.length}</Badge> : null}
          >
            <div className="p-3">
              {versions.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">No versions yet.</p>
              ) : (
                <div className="space-y-2">
                  {versions.slice().reverse().map((v) => (
                    <div key={v.id} className="flex items-center gap-2 p-2 rounded-lg border bg-muted/30 overflow-hidden" data-testid={`version-${v.id}`}>
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <FileText className="h-3.5 w-3.5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium break-words">{v.description}</p>
                        <p className="text-[10px] text-muted-foreground break-words">{v.createdByName} &middot; {formatDate(v.createdAt)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CollapsibleCard>

          {/* Conversation History Card */}
          <CollapsibleCard
            title="Conversation History"
            icon={MessageSquare}
            defaultOpen={true}
            testId="card-conversation"
            badge={sectionComments.length > 0 ? <Badge variant="secondary" className="h-4 px-1 text-[10px]">{sectionComments.length}</Badge> : null}
          >
            <div>
              <div className="p-3 space-y-3">
                {sectionComments.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-4">No conversation yet.</p>
                ) : (
                  sectionComments.map(c => (
                    <div key={c.id} className="rounded-lg border p-2.5 text-sm space-y-2" data-testid={`comment-${c.id}`}>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary">
                          {c.authorName?.charAt(0)}
                        </div>
                        <span className="font-medium text-xs">{c.authorName}</span>
                        <span className="text-[10px] text-muted-foreground ml-auto">{getRelativeTime(c.createdAt)}</span>
                      </div>
                      <p className="text-xs">{c.text}</p>
                      {c.status === 'OPEN' && (
                        <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2" onClick={() => onResolveComment(c.id)} data-testid={`resolve-comment-${c.id}`}>
                          <Check className="h-3 w-3 mr-1" /> Resolve
                        </Button>
                      )}
                      {c.status === 'RESOLVED' && (
                        <Badge variant="outline" className="text-[10px] text-green-600 border-green-200"><Check className="h-3 w-3 mr-1" />Resolved</Badge>
                      )}
                      {c.replies?.length > 0 && (
                        <div className="pl-4 border-l-2 border-muted space-y-2 mt-1">
                          {c.replies.map(r => (
                            <div key={r.id} className="text-xs space-y-1" data-testid={`reply-${r.id}`}>
                              <div className="flex items-center gap-2">
                                <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center text-[9px] font-medium text-primary shrink-0">
                                  {r.authorName?.charAt(0)}
                                </div>
                                <span className="font-medium">{r.authorName}</span>
                                <span className="text-[10px] text-muted-foreground ml-auto">{getRelativeTime(r.createdAt)}</span>
                              </div>
                              <p className="pl-7">{r.text}</p>
                            </div>
                          ))}
                        </div>
                      )}
                      {replyTargetId === c.id ? (
                        <div className="flex gap-1.5 mt-1">
                          <Input value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Write a reply..." className="h-7 text-xs" onKeyDown={e => e.key === 'Enter' && handleReply(c.id)} data-testid={`reply-input-${c.id}`} />
                          <Button size="sm" className="h-7 px-2 text-xs" onClick={() => handleReply(c.id)}>Send</Button>
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => { setReplyTargetId(null); setReplyText(''); }}><X className="h-3 w-3" /></Button>
                        </div>
                      ) : (
                        <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2" onClick={() => setReplyTargetId(c.id)}>
                          <Reply className="h-3 w-3 mr-1" /> Reply
                        </Button>
                      )}
                    </div>
                  ))
                )}
              </div>
              {/* Add to conversation */}
              <div className="p-3 border-t">
                <div className="flex gap-1.5">
                  <Input
                    value={newComment}
                    onChange={e => setNewComment(e.target.value)}
                    placeholder="Write a message..."
                    className="h-8 text-xs"
                    onKeyDown={e => e.key === 'Enter' && handleAddComment()}
                    data-testid="add-comment-input"
                  />
                  <Button size="sm" className="h-8 px-3" onClick={handleAddComment} disabled={!newComment.trim()} data-testid="add-comment-btn">
                    <Send className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
          </CollapsibleCard>

        </div>
      </ScrollArea>
    </div>
  );
}

/* ── Submit for Review Dialog ── */
function SubmitDialog({ open, onOpenChange, onSubmit }) {
  const [reviewerId, setReviewerId] = useState('');
  const reviewers = MOCK_USERS.filter(u => u.role === USER_ROLES.REVIEWER && u.status === 'ACTIVE');
  const selected = reviewers.find(r => r.id === reviewerId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="submit-review-dialog">
        <DialogHeader>
          <DialogTitle>Submit for Review</DialogTitle>
          <DialogDescription>Select a reviewer. Editing will be locked until the review is complete.</DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <Select value={reviewerId} onValueChange={setReviewerId}>
            <SelectTrigger data-testid="reviewer-select"><SelectValue placeholder="Select a reviewer..." /></SelectTrigger>
            <SelectContent>
              {reviewers.map(r => (
                <SelectItem key={r.id} value={r.id}>{r.name} — {r.department}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => { onSubmit(selected.id, selected.name); onOpenChange(false); }} disabled={!reviewerId} data-testid="confirm-submit-btn">
            <Send className="h-4 w-4 mr-1" /> Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Main Document Editor ── */
export function DocumentEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, isAdmin, isUser, isReviewer } = useAuth();
  const {
    getDocument,
    updateSection,
    markSectionComplete,
    generateSectionContent,
    submitForReview,
    approveDocument,
    rejectDocument,
    addComment,
    replyToComment,
    resolveComment,
    getComments,
    getVersionHistory,
  } = useDocuments();

  const [leftOpen, setLeftOpen] = useState(true);
  const [rightOpen, setRightOpen] = useState(true);
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editorHtml, setEditorHtml] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [inlineComments, setInlineComments] = useState([]);
  const [doc003Headings, setDoc003Headings] = useState([]);
  const [doc003ActiveHeading, setDoc003ActiveHeading] = useState(null);
  const [doc003Comments, setDoc003Comments] = useState([]);
  const [doc003ActiveComment, setDoc003ActiveComment] = useState(null);
  const [doc003SetActiveComment, setDoc003SetActiveComment] = useState(null);
  const [doc003CommentActions, setDoc003CommentActions] = useState(null);

  const handleDoc003CommentsChange = useCallback((comments, activeId, setActiveId, actions) => {
    setDoc003Comments(comments);
    setDoc003ActiveComment(activeId);
    setDoc003SetActiveComment(() => setActiveId);
    if (actions) setDoc003CommentActions(actions);
  }, []);

  const doc = getDocument(id);
  const comments = useMemo(() => (doc ? getComments(doc.id) : []), [doc, getComments]);
  const versions = useMemo(() => (doc ? getVersionHistory(doc.id) : []), [doc, getVersionHistory]);
  const sections = doc?.sections || [];

  // Load pre-existing inline comments for in-review documents
  const docInlineComments = useMemo(() => {
    if (!doc) return [];
    const mock = MOCK_INLINE_COMMENTS[doc.id] || [];
    return [...mock, ...inlineComments];
  }, [doc, inlineComments]);

  // Build HTML for editor from all sections (with inline comment marks)
  const buildEditorHtml = useCallback(() => {
    if (!sections.length) return '';
    return sections.map(s => {
      let contentHtml = s.content
        ? `<p>${s.content.replace(/\n\n/g, '</p><p>').replace(/\n/g, '<br>')}</p>`
        : '<p></p>';

      // Apply inline comment marks to content
      const sectionComments = docInlineComments.filter(c => c.sectionId === s.id);
      sectionComments.forEach(c => {
        if (c.selectedText && contentHtml.includes(c.selectedText)) {
          contentHtml = contentHtml.replace(
            c.selectedText,
            `<span data-inline-comment="${c.id}" class="inline-comment-highlight">${c.selectedText}</span>`
          );
        }
      });

      let html = `<h2>${s.title}</h2>${contentHtml}`;
      if (s.images && s.images.length > 0) {
        html += s.images.map(img =>
          `<figure><img src="${img.src}" alt="${img.caption || ''}" />${img.caption ? `<figcaption>${img.caption}</figcaption>` : ''}</figure>`
        ).join('');
      }
      return html;
    }).join('');
  }, [sections, docInlineComments]);

  // Permissions — edit mode available for all document statuses
  const canEdit = useMemo(() => {
    if (!doc) return false;
    if (isAdmin) return true;
    if (isUser && doc.createdBy === currentUser?.id) return true;
    if (isReviewer && doc.assignedReviewer === currentUser?.id) return true;
    return false;
  }, [doc, isAdmin, isUser, isReviewer, currentUser]);

  const canReview = useMemo(() => {
    if (!doc) return false;
    if (isReviewer && doc.assignedReviewer === currentUser?.id) {
      return [DOCUMENT_STATUS.PENDING_REVIEW, DOCUMENT_STATUS.IN_REVIEW].includes(doc.status);
    }
    if (isAdmin) return [DOCUMENT_STATUS.PENDING_REVIEW, DOCUMENT_STATUS.IN_REVIEW].includes(doc.status);
    return false;
  }, [doc, isReviewer, isAdmin, currentUser]);

  const canSubmit = useMemo(() => {
    if (!doc) return false;
    if (isAdmin || (isUser && doc.createdBy === currentUser?.id)) {
      return [DOCUMENT_STATUS.DRAFT, DOCUMENT_STATUS.IN_PROGRESS, DOCUMENT_STATUS.REJECTED].includes(doc.status);
    }
    return false;
  }, [doc, isAdmin, isUser, currentUser]);

  // Handlers
  const handleSelectSection = useCallback((sectionId) => {
    setActiveSectionId(sectionId);
    if (!isEditMode) {
      const el = document.getElementById(`section-${sectionId}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [isEditMode]);

  const handleSwitchToEdit = useCallback(() => {
    setEditorHtml(buildEditorHtml());
    setIsEditMode(true);
  }, [buildEditorHtml]);

  const handleSwitchToRead = useCallback(() => {
    setIsEditMode(false);
  }, []);

  const handleEditorUpdate = useCallback((html) => {
    setEditorHtml(html);
  }, []);

  const handleSave = useCallback(() => {
    if (!doc || !editorHtml) return;
    setIsSaving(true);

    // Parse HTML back to sections by splitting on h2 tags
    const parser = new DOMParser();
    const parsed = parser.parseFromString(editorHtml, 'text/html');
    const headings = parsed.querySelectorAll('h2');

    headings.forEach((h2, idx) => {
      if (idx < sections.length) {
        // Collect all sibling nodes until next h2
        let content = '';
        let sibling = h2.nextElementSibling;
        while (sibling && sibling.tagName !== 'H2') {
          content += sibling.textContent + '\n';
          sibling = sibling.nextElementSibling;
        }
        updateSection(doc.id, sections[idx].id, {
          content: content.trim(),
          status: content.trim() ? SECTION_STATUS.IN_PROGRESS : SECTION_STATUS.NOT_STARTED,
        });
      }
    });

    setIsSaving(false);
    toast.success('Document saved');
  }, [doc, editorHtml, sections, updateSection]);

  const handleGenerate = useCallback(async () => {
    if (!doc) return;
    setIsGenerating(true);
    // Generate for active section or first section
    const sectionId = activeSectionId || sections[0]?.id;
    if (sectionId) {
      const result = await generateSectionContent(doc.id, sectionId);
      if (result.success) {
        // Rebuild editor HTML with updated content
        setTimeout(() => {
          setEditorHtml(buildEditorHtml());
          toast.success(`Content generated (${result.confidenceScore}% confidence)`);
        }, 100);
      }
    }
    setIsGenerating(false);
  }, [doc, activeSectionId, sections, generateSectionContent, buildEditorHtml]);

  const handleAddInlineComment = useCallback((commentData) => {
    setInlineComments(prev => [...prev, commentData]);
  }, []);

  const handleSubmitForReview = useCallback((reviewerId, reviewerName) => {
    if (!doc) return;
    submitForReview(doc.id, reviewerId, reviewerName);
    toast.success('Document submitted for review');
  }, [doc, submitForReview]);

  const handleApprove = useCallback(() => {
    if (!doc) return;
    approveDocument(doc.id);
    toast.success('Document approved');
  }, [doc, approveDocument]);

  const handleReject = useCallback(() => {
    if (!doc) return;
    rejectDocument(doc.id);
    toast.success('Document rejected');
  }, [doc, rejectDocument]);

  const handleAddComment = useCallback((commentData) => {
    if (!doc) return;
    addComment(doc.id, commentData);
  }, [doc, addComment]);

  const handleReplyComment = useCallback((commentId, replyData) => {
    if (!doc) return;
    replyToComment(doc.id, commentId, replyData);
  }, [doc, replyToComment]);

  const handleResolveComment = useCallback((commentId) => {
    if (!doc) return;
    resolveComment(doc.id, commentId);
    toast.success('Comment resolved');
  }, [doc, resolveComment]);

  // Not found
  if (!doc) {
    return (
      <BaseLayout title="Document Not Found">
        <div className="flex flex-col items-center justify-center h-[60vh]" data-testid="document-not-found">
          <FileText className="h-12 w-12 text-muted-foreground/40 mb-3" />
          <p className="text-lg font-medium">Document not found</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate(ROUTES.DOCUMENTS)}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Documents
          </Button>
        </div>
      </BaseLayout>
    );
  }

  return (
    <BaseLayout title="" fullWidth>
      <div className="flex flex-col h-[calc(100vh-64px)]" data-testid="document-editor-page">
        {/* Top Bar */}
        <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b bg-background shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => navigate(ROUTES.DOCUMENTS)} data-testid="back-btn">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-sm font-semibold truncate" data-testid="document-title">{doc.title}</h1>
              <div className="flex items-center gap-2">
                <DocumentStatusBadge status={doc.status} size="sm" />
                <span className="text-xs text-muted-foreground">{doc.progress}% complete</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* Collaborator avatars */}
            <CollaboratorAvatars doc={doc} />
            {/* Role-based actions */}
            {canSubmit && (
              <Button size="sm" onClick={() => setSubmitOpen(true)} data-testid="submit-for-review-btn">
                <Send className="h-4 w-4 mr-1" /> Submit for Review
              </Button>
            )}
            {canReview && (
              <>
                <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400" onClick={handleReject} data-testid="reject-btn">
                  <ThumbsDown className="h-4 w-4 mr-1" /> Reject
                </Button>
                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={handleApprove} data-testid="approve-btn">
                  <ThumbsUp className="h-4 w-4 mr-1" /> Approve
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Three-panel layout */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left Panel */}
          {leftOpen ? (
            <div className="w-64 border-r bg-card shrink-0 overflow-hidden" data-testid="left-panel">
              {doc?.id === 'DOC-003' ? (
                <Doc003DynamicToc
                  headings={doc003Headings}
                  activeId={doc003ActiveHeading}
                  onSelect={setDoc003ActiveHeading}
                  onCollapse={() => setLeftOpen(false)}
                />
              ) : (
                <TableOfContents
                  sections={sections}
                  activeId={activeSectionId || sections[0]?.id}
                  onSelect={handleSelectSection}
                  progress={doc.progress}
                  onCollapse={() => setLeftOpen(false)}
                />
              )}
            </div>
          ) : (
            <CollapsedLeftStrip onExpand={() => setLeftOpen(true)} />
          )}

          {/* Center Panel */}
          <div className="flex-1 overflow-hidden">
            <CenterPanel
              doc={doc}
              sections={sections}
              isEditMode={isEditMode}
              editorHtml={editorHtml}
              onEditorUpdate={handleEditorUpdate}
              onAddInlineComment={handleAddInlineComment}
              inlineComments={docInlineComments}
              authorName={currentUser?.name || 'User'}
              onSwitchToEdit={handleSwitchToEdit}
              onSwitchToRead={handleSwitchToRead}
              onSave={handleSave}
              onGenerate={handleGenerate}
              isSaving={isSaving}
              isGenerating={isGenerating}
              canEdit={canEdit}
              onScrollToSection={handleSelectSection}
              activeSectionId={activeSectionId}
              onDoc003TocReady={setDoc003Headings}
              onDoc003CommentsChange={handleDoc003CommentsChange}
              currentUser={currentUser}
            />
          </div>

          {/* Right Panel */}
          {rightOpen ? (
            <div className="w-72 border-l bg-card shrink-0 overflow-hidden" data-testid="right-panel">
              {doc?.id === 'DOC-003' ? (
                <Doc003CommentSidebar
                  comments={doc003Comments}
                  activeCommentId={doc003ActiveComment}
                  onSetActive={(id) => doc003SetActiveComment && doc003SetActiveComment(id)}
                  onReply={(id, text) => doc003CommentActions?.handleReply(id, text)}
                  onResolve={(id) => doc003CommentActions?.handleResolve(id)}
                  onDelete={(id) => doc003CommentActions?.handleDelete(id)}
                  currentUser={currentUser}
                  onCollapse={() => setRightOpen(false)}
                />
              ) : (
                <DetailPanel
                  onCollapse={() => setRightOpen(false)}
                  document={doc}
                  comments={comments}
                  versions={versions}
                  onAddComment={handleAddComment}
                  onReplyComment={handleReplyComment}
                  onResolveComment={handleResolveComment}
                  currentUser={currentUser}
                  activeSectionId={activeSectionId || sections[0]?.id}
                  sections={sections}
                />
              )}
            </div>
          ) : (
            <CollapsedRightStrip onExpand={() => setRightOpen(true)} />
          )}
        </div>
      </div>

      {/* Submit Dialog */}
      <SubmitDialog open={submitOpen} onOpenChange={setSubmitOpen} onSubmit={handleSubmitForReview} />
    </BaseLayout>
  );
}

export default DocumentEditor;
